<template>
  <div v-overlay="closeDropdown">
    <div
      class="c-dropdown"
      @keydown.escape="toggleDropdown"
    >
      <div
        data-test="dropdown-wrapper"
        class="c-dropdown__wrapper"
        tabindex="0"
        as="button"
        :class="[
          { 'c-dropdown__wrapper--selected': selected },
          { 'c-dropdown__wrapper--error': errorMessage },
          { 'c-dropdown__wrapper--open': isOpen },
          {
            'c-dropdown__wrapper--none-editable': disabled || !options.length,
          },
        ]"
        @keydown.enter.self="toggleDropdown"
        @click="toggleDropdown"
      >
        <div :class="selected ? 'label--selected-wrapper' : 'label__wrapper'">
          <p
            :class="
              selected ? 'label--selected-placeholder' : 'label__placeholder'
            "
          >
            {{ label }}
          </p>

          <p
            v-if="selected"
            class="label__option-text"
            data-test="selected-label"
          >
            {{ selectedLabel }}
          </p>
        </div>
        <div>
          <CIcon
            :name="isOpen ? 'expand_less' : 'expand_more'"
            :class="
              disabled || !options.length ? 'text-gray-400' : 'text-gray-800'
            "
            class="c-icon"
          />
        </div>
      </div>
      <transition name="slide">
        <div
          v-if="isOpen"
          :class="{ 'option-list--is-open-upwards': isOpenedUpwards }"
          data-test="option-list"
          class="option-list items-transitions"
        >
          <div
            v-for="op of options"
            :key="op.value"
            :class="op.value === selected && 'option__wrapper--selected-option'"
            tabindex="0"
            :data-test="'option-' + op.value"
            class="option__wrapper"
            @keydown.enter.self="selectOption(op.value, $event)"
            @click="selectOption(op.value, $event)"
          >
            <div class="option__text">
              {{ op.label }}
            </div>
            <CIcon
              v-if="op.value === selected"
              class="text-gray-800"
              name="check"
            />
          </div>
        </div>
      </transition>
      <div
        v-if="errorMessage || helperText"
        :class="{ 'message--error': errorMessage }"
        class="message"
      >
        <p>{{ errorMessage ? errorMessage : helperText }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CIcon from '../../atoms/CIcon'
import { computed, ref } from 'vue'

interface Option {
  label: string
  value: string
}

interface Props {
  label: string
  options: Option[]
  value: string
  isOpenedUpwards?: boolean
  errorMessage?: string
  helperText?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpenedUpwards: false,
  errorMessage: '',
  helperText: '',
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:model-value', value: string): void
}>()

const isOpen = ref(false)
const eventTimeStamp = ref<number | undefined>(undefined)

const selected = computed(() => props.value)
const selectedLabel = computed(() => {
  return props.options.find((op) => op.value === selected.value)?.label || ''
})

const selectOption = (option: string, event: Event) => {
  toggleDropdown(event)
  emit('update:model-value', option)
}

const toggleDropdown = (event: Event) => {
  // hack to prevent the document eventlistener getting called with the same event as when registering the listener
  if (event.timeStamp === eventTimeStamp.value) {
    return
  }
  eventTimeStamp.value = event.timeStamp
  isOpen.value = !isOpen.value

  if (isOpen.value) {
    document.addEventListener('click', toggleDropdown)
  } else {
    document.removeEventListener('click', toggleDropdown)
  }
}

const closeDropdown = () => {
  if (isOpen.value) {
    isOpen.value = false
  }
}
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CDropdown',
})
</script>

<style lang="scss" scoped>
.items-transitions {
  transition: all 0.1s ease-in-out 0s;
}
</style>
